import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  ChartBarIcon,
  TagIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { Trend, TrendFilters, PaginatedResponse } from '../types';

const TrendsPage: React.FC = () => {
  const { user } = useAuth();
  const { isFeatureEnabled, setLoading, addNotification } = useApp();
  const [trends, setTrends] = useState<Trend[]>([]);
  const [loading, setLocalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<TrendFilters>({});
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'trending_score' | 'engagement_score' | 'growth_rate' | 'created_at'>('trending_score');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const loadTrends = useCallback(async () => {
    setLocalLoading(true);
    setLoading('trends', true);
    try {
      const response: PaginatedResponse<Trend> = await apiService.getTrends({
        ...filters,
        sort_by: sortBy,
        sort_order: sortOrder
      });
      setTrends(response.data);
      setTotalPages(response.pagination.total_pages);
    } catch (error) {
      console.error('Error loading trends:', error);
      addNotification({
        id: 'trends-error',
        type: 'error',
        title: 'Error Loading Trends',
        message: 'Failed to load trends. Please try again.',
        timestamp: new Date().toISOString(),
        read: false,
        category: 'system'
      });
    } finally {
      setLocalLoading(false);
      setLoading('trends', false);
    }
  }, [filters, sortBy, sortOrder, setLoading, addNotification]);

  const loadSuggestions = useCallback(async () => {
    try {
      const suggestions = await apiService.getTrendSuggestions(searchQuery);
      setSuggestions(suggestions);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    }
  }, [searchQuery]);

  useEffect(() => {
    // Don't load data if user is not authenticated
    if (!user) {
      return;
    }
    loadTrends();
  }, [user, loadTrends]);

  useEffect(() => {
    if (searchQuery.length > 2) {
      loadSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, loadSuggestions]);

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      setLocalLoading(true);
      try {
        const response = await apiService.searchTrends(searchQuery, filters);
        setTrends(response.data);
        setTotalPages(response.pagination.total_pages);
        setCurrentPage(1);
      } catch (error) {
        console.error('Error searching trends:', error);
      } finally {
        setLocalLoading(false);
      }
    }
  };

  const handleFilterChange = (key: keyof TrendFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setCurrentPage(1);
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'fashion':
        return 'bg-pink-100 text-pink-800';
      case 'beauty':
        return 'bg-purple-100 text-purple-800';
      case 'lifestyle':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMomentumColor = (score: number) => {
    if (score > 70) return 'text-green-600';
    if (score > 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getViralPotentialColor = (score: number) => {
    if (score > 0.8) return 'text-purple-600';
    if (score > 0.6) return 'text-blue-600';
    return 'text-gray-600';
  };

  const TrendCard: React.FC<{ trend: Trend }> = ({ trend }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-2">{trend.trend_name}</h3>
          <div className="flex items-center space-x-2 mb-3">
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(trend.category)}`}>
              {trend.category}
            </span>
            {trend.subcategory && (
              <span className="text-xs text-gray-500">{trend.subcategory}</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center space-x-1 mb-1">
            <ArrowUpIcon className="h-4 w-4 text-green-500" />
            <span className="text-sm font-medium text-green-600">
              {trend.momentum_score}%
            </span>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full ${getMomentumColor(trend.momentum_score)} bg-opacity-10`}>
            {trend.momentum_classification}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-600">Engagement</p>
          <p className="text-lg font-semibold text-gray-900">{trend.total_engagement.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Viral Potential</p>
          <p className={`text-lg font-semibold ${getViralPotentialColor(trend.viral_potential)}`}>
            {(trend.viral_potential * 100).toFixed(0)}%
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex space-x-1">
          {trend.platforms.slice(0, 3).map((platform) => (
            <span
              key={platform.platform}
              className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center text-xs font-medium text-gray-600"
            >
              {platform.platform.charAt(0).toUpperCase()}
            </span>
          ))}
          {trend.platforms.length > 3 && (
            <span className="text-xs text-gray-500">+{trend.platforms.length - 3}</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {isFeatureEnabled('canAccessBusinessIntelligence') && (
            <button className="p-1 text-gray-400 hover:text-blue-600">
              <ChartBarIcon className="h-4 w-4" />
            </button>
          )}
          <Link
            to={`/trends/${trend.id}`}
            className="flex items-center text-sm text-purple-600 hover:text-purple-500 font-medium"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View Details
          </Link>
        </div>
      </div>

      {trend.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {trend.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700"
            >
              <TagIcon className="h-3 w-3 mr-1" />
              {tag}
            </span>
          ))}
          {trend.tags.length > 3 && (
            <span className="text-xs text-gray-500">+{trend.tags.length - 3} more</span>
          )}
        </div>
      )}
    </div>
  );

  const TrendListItem: React.FC<{ trend: Trend }> = ({ trend }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">{trend.trend_name}</h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(trend.category)}`}>
                {trend.category}
              </span>
              <span className="text-sm text-gray-500">{trend.total_engagement.toLocaleString()} engagement</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-6">
          <div className="text-center">
            <p className="text-sm text-gray-600">Momentum</p>
            <p className={`font-semibold ${getMomentumColor(trend.momentum_score)}`}>
              {trend.momentum_score}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Viral Potential</p>
            <p className={`font-semibold ${getViralPotentialColor(trend.viral_potential)}`}>
              {(trend.viral_potential * 100).toFixed(0)}%
            </p>
          </div>
          <Link
            to={`/trends/${trend.id}`}
            className="flex items-center text-sm text-purple-600 hover:text-purple-500 font-medium"
          >
            <EyeIcon className="h-4 w-4 mr-1" />
            View
          </Link>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trends</h1>
          <p className="text-gray-600">Discover and analyze trending topics across platforms</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Grid view"
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="List view"
            >
              <ListBulletIcon className="h-4 w-4" />
            </button>
          </div>
          {isFeatureEnabled('canCreateCustomAlerts') && (
            <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
              Create Alert
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search trends..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-10 mt-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      setSearchQuery(suggestion);
                      setSuggestions([]);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 rounded-lg border ${
              showFilters ? 'bg-purple-50 border-purple-200 text-purple-700' : 'border-gray-300 text-gray-700'
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={handleSearch}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Search
          </button>
        </div>

        {showFilters && (
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value || undefined)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Categories</option>
                  <option value="fashion">Fashion</option>
                  <option value="beauty">Beauty</option>
                  <option value="lifestyle">Lifestyle</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Status</option>
                  <option value="trending">Trending</option>
                  <option value="emerging">Emerging</option>
                  <option value="stable">Stable</option>
                  <option value="declining">Declining</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Min Engagement</label>
                <input
                  type="number"
                  value={filters.min_engagement || ''}
                  onChange={(e) => handleFilterChange('min_engagement', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500"
                >
                  <option value="trending_score">Trending Score</option>
                  <option value="engagement_score">Engagement</option>
                  <option value="growth_rate">Growth Rate</option>
                  <option value="created_at">Date Created</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear all filters
              </button>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSortOrder('asc')}
                  className={`p-1 rounded ${sortOrder === 'asc' ? 'bg-purple-100 text-purple-700' : 'text-gray-400'}`}
                >
                  <ArrowUpIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setSortOrder('desc')}
                  className={`p-1 rounded ${sortOrder === 'desc' ? 'bg-purple-100 text-purple-700' : 'text-gray-400'}`}
                >
                  <ArrowDownIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading trends...</p>
          </div>
        ) : trends.length === 0 ? (
          <div className="text-center py-12">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No trends found</h3>
            <p className="text-gray-600">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {trends.length} trends
              </p>
            </div>
            
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {trends.map((trend) => (
                viewMode === 'grid' ? (
                  <TrendCard key={trend.id} trend={trend} />
                ) : (
                  <TrendListItem key={trend.id} trend={trend} />
                )
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrendsPage; 